import { faCircleQuestion, faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import { FieldTypes, type Field } from '@/components/PersonInPlaceEditor/types';
import AppContext from '@/contexts/appContext';
import RemitContext from '@/pages/EditRemit/contexts/remitContext';
import { RemitAdjustmentSchema } from '@/pages/EditRemit/schemas/RemitAdjustmentSchema';
import { RemitRemarkSchema } from '@/pages/EditRemit/schemas/RemitRemarkSchema';
import RemitAdjustmentRepository from '@/repositories/RemitAdjustmentRepository';
import RemitRemarkRepository from '@/repositories/RemitRemarkRepository';
import {
  CagcCodeRegistry,
  CarcCodeRegistry,
  RarcCodeRegistry,
  RemitAdjustmentPayload,
  RemitRemarkPayload
} from '@/types/billing';
import { encounterTypeMapping } from '@/types/encounter';
import Tooltip from '@/ui/components/Tooltip';
import { currencyInCentsFormatter } from '@/utils/currencies';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import Debug from '../Debug';
import InPlaceEditor from '../PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { RecordType } from '../PersonInPlaceEditor/components/InPlaceEditor/types';

import { RemitServiceLineCardProps } from './types';

const RemitServiceLineCard = ({ serviceLine, editable }: RemitServiceLineCardProps) => {
  // Line Item:  Svc Date   CPT    Charge Amt   Payment Amt  Total Adj Amt  Remarks
  const { addMessage } = useContext(AppContext);
  const { refetch: refetchRemit, highlightServiceLineId } = useContext(RemitContext);
  const elementRef = useRef(null);

  const highlight = useMemo(() => {
    return String(highlightServiceLineId) === String(serviceLine.id);
  }, [highlightServiceLineId, serviceLine.id]);

  useEffect(() => {
    if (elementRef.current && highlight) {
      (elementRef.current as HTMLElement).scrollIntoView({ behavior: 'smooth' });
    }
  }, [highlight]);

  const hasRemarks = serviceLine?.remarks && serviceLine.remarks.length > 0;
  const hasAdjustments = serviceLine?.adjustments && serviceLine.adjustments.length > 0;
  const hasBillingItems = serviceLine?.billing_items && serviceLine.billing_items.length > 0;

  const formattedCarcCodes = useMemo(() => {
    return Object.keys(CarcCodeRegistry).map((key) => ({
      value: key,
      label: `${key} - ${CarcCodeRegistry[key as keyof typeof CarcCodeRegistry]?.description}`
    }));
  }, []);

  const formattedCagcCodes = useMemo(() => {
    return Object.keys(CagcCodeRegistry).map((key) => ({
      value: key,
      label: `${key} - ${CagcCodeRegistry[key as keyof typeof CagcCodeRegistry]?.description}`
    }));
  }, []);

  const remitAdjustmentFields: Field<RemitAdjustmentPayload>[] = [
    {
      alwaysSend: true,
      key: 'remit_service_line',
      label: 'Remit',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: true,
      key: 'group_code',
      label: 'Group Code',
      type: FieldTypes.SELECT,
      options: formattedCagcCodes
    },
    {
      alwaysSend: true,
      key: 'reason_code',
      label: 'Reason Code',
      type: FieldTypes.SELECT,
      options: formattedCarcCodes
    },
    {
      alwaysSend: true,
      key: 'amount',
      label: 'Amount',
      type: FieldTypes.CURRENCY
    }
  ];

  const formattedRarcCodes = useMemo(() => {
    return Object.keys(RarcCodeRegistry).map((key) => ({
      value: key,
      label: `${key} - ${RarcCodeRegistry[key as keyof typeof RarcCodeRegistry]?.description}`
    }));
  }, []);
  const remitRemarkFields: Field<RemitRemarkPayload>[] = [
    {
      alwaysSend: true,
      key: 'remit_service_line',
      label: 'Remit',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: true,
      key: 'qualifier',
      label: 'Qualifier',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'code',
      label: 'Code',
      type: FieldTypes.SELECT,
      options: formattedRarcCodes
    }
  ];
  const submittedRemitAdjustmentValuesManipulator = async (
    values: Partial<RemitAdjustmentPayload>
  ) => {
    return values as RemitAdjustmentPayload;
  };
  const remitAdjustmentRepo = RemitAdjustmentRepository();

  const mutateRemitAdjustment = useMutation({
    mutationFn: async ({
      id,
      values
    }: {
      id?: string;
      values: Partial<RemitAdjustmentPayload>;
    }) => {
      if (!id) {
        return await remitAdjustmentRepo.create({ data: values as RemitAdjustmentPayload });
      }
      return await remitAdjustmentRepo.update({ id, data: values as RemitAdjustmentPayload });
    },
    onSuccess: async () => {
      if (refetchRemit) await refetchRemit();
      addMessage('success', 'Remit Service Line saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteRemitAdjustment = useMutation({
    mutationFn: async (id: string) => {
      await remitAdjustmentRepo.delete({ id });
    },
    onSuccess: async () => {
      if (refetchRemit) await refetchRemit();
      addMessage('success', 'Remit Service Line deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submittedRemitRemarkValuesManipulator = async (values: Partial<RemitRemarkPayload>) => {
    return values as RemitRemarkPayload;
  };
  const remitRemarkRepo = RemitRemarkRepository();

  const mutateRemitRemark = useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: Partial<RemitRemarkPayload> }) => {
      if (!id) {
        return await remitRemarkRepo.create({ data: values as RemitRemarkPayload });
      }
      return await remitRemarkRepo.update({ id, data: values as RemitRemarkPayload });
    },
    onSuccess: async () => {
      if (refetchRemit) await refetchRemit();
      addMessage('success', 'Remit Service Line saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteRemitRemark = useMutation({
    mutationFn: async (id: string) => {
      await remitRemarkRepo.delete({ id });
    },
    onSuccess: async () => {
      if (refetchRemit) await refetchRemit();
      addMessage('success', 'Remit Service Line deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
  return (
    <div
      className={`border border-b-2 p-2 mt-2 ${highlight ? 'bg-yellow-50' : ''}`}
      ref={elementRef}>
      <div className="flex flex-row ">
        <div className="flex-1">
          <div className="text-sm text-gray-600">Service Date</div>
          <div>
            {serviceLine.start_on}
            {serviceLine.start_on !== serviceLine.end_on && (
              <>
                - {serviceLine.end_on}
                <span className="text-xs text-gray-500"> (Range)</span>
              </>
            )}
          </div>
        </div>
        <div className="flex-1">
          <div className="text-sm text-gray-600">CPT</div>
          <div>{serviceLine.code}</div>
        </div>

        <div className="flex-1">
          <div className="text-sm text-gray-600">Modifier</div>
          <div>{serviceLine.modifier}</div>
        </div>
        <div className="flex-1">
          <div className="text-sm text-gray-600">Charge Amt</div>
          <div>{currencyInCentsFormatter(serviceLine.charge_amount_in_cents || 0)}</div>
        </div>
        <div className="flex-1">
          <div className="text-sm text-gray-600">Payment Amt</div>
          {/* Small status light, red for no payment, yellow for under payment, and green for charge payment */}

          <span className="flex items-center">
            <div className="mr-2">
              {currencyInCentsFormatter(serviceLine.paid_amount_in_cents || 0)}
            </div>
            {serviceLine.paid_amount_in_cents === 0 ? (
              <Tooltip tooltipContent="No Payment">
                <span className="inline-block w-3 h-3 bg-red-500 rounded-full"></span>
              </Tooltip>
            ) : (serviceLine.paid_amount_in_cents || 0) <
              (serviceLine.charge_amount_in_cents || 0) ? (
              <Tooltip tooltipContent="Under Payment">
                <span className="inline-block w-3 h-3 bg-yellow-500 rounded-full"></span>
              </Tooltip>
            ) : (
              <Tooltip tooltipContent="Full Payment">
                <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
              </Tooltip>
            )}
            {(serviceLine.paid_amount_in_cents || 0) >
              (serviceLine.charge_amount_in_cents || 0) && (
              <Tooltip tooltipContent="Over Payment">
                <span className="inline-block ml-1 w-3 h-3 bg-blue-500 rounded-full"></span>
              </Tooltip>
            )}
          </span>
        </div>
        <div className="flex-1">
          <div className="text-sm text-gray-600">Total Adj Amt</div>
          <div>
            {currencyInCentsFormatter(
              (serviceLine?.adjustments || []).reduce(
                (acc, adj) => acc + (adj.amount_in_cents || 0),
                0
              )
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-row">
        <div data-testid="remarks" className="flex-1">
          {/* Remarks */}
          {(hasRemarks || editable) && <div className="text-sm text-gray-600">Remarks</div>}
          {serviceLine?.remarks?.map((remark) => {
            const remarkReg =
              RarcCodeRegistry[String(remark.code) as keyof typeof RarcCodeRegistry];
            return (
              <div className="relative" key={remark.id}>
                <InPlaceEditor
                  deleteMutation={deleteRemitRemark}
                  disableEdit={!editable}
                  fields={remitRemarkFields}
                  key={serviceLine.id}
                  mutation={mutateRemitRemark}
                  record={
                    {
                      ...remark,
                      remit_service_line: remark.id
                    } as RemitRemarkPayload
                  }
                  recordType={RecordType.RemitRemark}
                  schema={RemitRemarkSchema}
                  submittedValuesManipulator={submittedRemitRemarkValuesManipulator}
                  subsectionEditor
                  sectionEditor>
                  {remark.qualifier}{' '}
                  <span className={remarkReg?.is_alert ? 'text-red-500' : 'text-gray-800'}>
                    <Tooltip tooltipContent={remarkReg?.description}>
                      {remark.code} <FontAwesomeIcon icon={faCircleQuestion} />
                    </Tooltip>
                  </span>
                </InPlaceEditor>
              </div>
            );
          })}
          {editable && (
            <InPlaceEditor
              deleteMutation={deleteRemitRemark}
              fields={remitRemarkFields}
              mutation={mutateRemitRemark}
              record={
                {
                  remit_service_line: serviceLine.id,
                  qualifier: 'HE'
                } as RemitRemarkPayload
              }
              recordType={RecordType.RemitClaim}
              schema={RemitRemarkSchema}
              newRecord={true}
              newRecordButtonText="Add Remark"
              submittedValuesManipulator={submittedRemitRemarkValuesManipulator}
              subsectionEditor></InPlaceEditor>
          )}
        </div>

        <div className="flex-1 mx-2">
          {/* Adjustments */}
          <div data-testid="adjustments" className="relative">
            {(hasAdjustments || editable) && (
              <div className="text-sm text-gray-600">Adjustments</div>
            )}
            {serviceLine?.adjustments?.map((adj) => (
              <div key={adj.id} className="relative">
                <InPlaceEditor
                  deleteMutation={deleteRemitAdjustment}
                  disableEdit={!editable}
                  fields={remitAdjustmentFields}
                  key={serviceLine.id}
                  mutation={mutateRemitAdjustment}
                  record={
                    {
                      ...adj,
                      remit_service_line: serviceLine.id
                    } as RemitAdjustmentPayload
                  }
                  recordType={RecordType.RemitAdjustment}
                  schema={RemitAdjustmentSchema}
                  sectionEditor
                  subsectionEditor
                  submittedValuesManipulator={submittedRemitAdjustmentValuesManipulator}>
                  <>
                    {currencyInCentsFormatter(adj.amount_in_cents || 0)}{' '}
                    <Tooltip
                      tooltipContent={
                        adj.group_code
                          ? CagcCodeRegistry[adj.group_code as keyof typeof CagcCodeRegistry]
                              ?.description
                          : adj.group_code
                      }>
                      {adj.group_code}
                    </Tooltip>
                    -
                    <Tooltip
                      tooltipContent={
                        adj.reason_code
                          ? CarcCodeRegistry[adj.reason_code as keyof typeof CarcCodeRegistry]
                              ?.description
                          : adj.reason_code
                      }>
                      {adj.reason_code} <FontAwesomeIcon icon={faCircleQuestion} />
                    </Tooltip>
                  </>
                </InPlaceEditor>
              </div>
            ))}
            {editable && (
              <InPlaceEditor
                deleteMutation={deleteRemitAdjustment}
                fields={remitAdjustmentFields}
                mutation={mutateRemitAdjustment}
                record={
                  {
                    remit_service_line: serviceLine.id
                  } as RemitAdjustmentPayload
                }
                recordType={RecordType.RemitClaim}
                schema={RemitAdjustmentSchema}
                newRecord={true}
                newRecordButtonText="Add Adjustment"
                submittedValuesManipulator={submittedRemitAdjustmentValuesManipulator}
                subsectionEditor></InPlaceEditor>
            )}
          </div>
        </div>
      </div>

      {/* Linked Billing Items */}
      {hasBillingItems && (
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faLink} className="text-blue-600" />
            <div className="text-sm font-medium text-gray-700">Linked Billing Items</div>
          </div>
          <div className="space-y-2 ml-6">
            {serviceLine.billing_items?.map((billingItem) => {
              // Extract the API type (without app name) from the full app type
              // e.g., 'appointments.appointment' -> 'appointment'
              const apiTypeKey = billingItem.encounter_type.includes('.')
                ? billingItem.encounter_type.split('.')[1]
                : billingItem.encounter_type;

              const mappedType =
                encounterTypeMapping[apiTypeKey as keyof typeof encounterTypeMapping];
              const encounterTypePath = mappedType?.path || apiTypeKey;

              return (
                <div
                  key={billingItem.id}
                  className="text-sm p-2 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100 transition-colors">
                  <Link
                    to={`/encounters/${encounterTypePath}/by_id/${billingItem.encounter_id}/billing`}
                    className="text-blue-600 hover:text-blue-800 font-medium block">
                    {billingItem.person_name || '?'} - {billingItem.service_type || '?'} on{' '}
                    {billingItem.service_at
                      ? (() => {
                          const d = new Date(billingItem.service_at);
                          return isNaN(d.getTime()) ? 'Unknown date' : d.toLocaleDateString();
                        })()
                      : 'Unknown date'}
                  </Link>
                  <Debug object={billingItem} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RemitServiceLineCard;
